import { EventManagerMixin } from '../utils/event-manager.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { CONFIG } from '../config.js';

/**
 * NoteEditor - Modal dialog for creating and editing hex notes
 *
 * Uses EventManagerMixin for automatic listener cleanup
 * Uses HexEvents for consistent event dispatching
 *
 * Events emitted:
 * - HexEvents.NOTE_SAVED: When a note is saved (bubbles, composed)
 */
class NoteEditor extends EventManagerMixin(HTMLElement) {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // State
    this.noteData = null;
    this.hexCoord = null;
    this.editMode = false;
    this.maxLength = CONFIG.NOTE_MAX_LENGTH;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  // disconnectedCallback is handled by EventManagerMixin - cleans up all listeners

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: ${CONFIG.Z_INDEX_MODAL};
          background: ${CONFIG.COLORS.OVERLAY_DARKER};
          align-items: center;
          justify-content: center;
        }

        :host([open]) {
          display: flex;
        }

        .dialog {
          background: var(--bg-elevated, #2a2a2a);
          border: 1px solid var(--border-default, #444);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-lg, 24px);
          max-width: 600px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-lg, 24px);
        }

        h2 {
          margin: 0;
          font-size: var(--text-xl, 1.25rem);
          color: var(--text-primary, #e5e5e5);
        }

        .close-btn {
          background: none;
          border: none;
          color: var(--text-secondary, #a0a0a0);
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-sm, 4px);
          transition: var(--transition-fast, 150ms ease-in-out);
        }

        .close-btn:hover {
          background: var(--bg-surface, #1a1a1a);
          color: var(--text-primary, #e5e5e5);
        }

        .form-group {
          margin-bottom: var(--space-md, 16px);
        }

        label {
          display: block;
          margin-bottom: var(--space-xs, 4px);
          color: var(--text-secondary, #a0a0a0);
          font-size: var(--text-sm, 0.875rem);
        }

        textarea {
          width: 100%;
          min-height: 200px;
          padding: var(--space-sm, 8px);
          background: var(--bg-input, #1f1f1f);
          border: 1px solid var(--border-subtle, #333);
          border-radius: var(--radius-sm, 4px);
          color: var(--text-primary, #e5e5e5);
          font-family: inherit;
          font-size: var(--text-base, 1rem);
          resize: vertical;
          box-sizing: border-box;
        }

        textarea:focus {
          outline: none;
          border-color: var(--color-primary, #3b82f6);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .char-counter {
          text-align: right;
          font-size: var(--text-sm, 0.875rem);
          color: var(--text-secondary, #a0a0a0);
          margin-top: var(--space-xs, 4px);
        }

        .char-counter.warning {
          color: var(--color-warning, #f59e0b);
        }

        .char-counter.error {
          color: var(--color-danger, #ef4444);
        }

        .visibility-group {
          display: flex;
          gap: var(--space-md, 16px);
          margin-bottom: var(--space-lg, 24px);
        }

        .radio-option {
          display: flex;
          align-items: center;
          cursor: pointer;
        }

        .radio-option input[type="radio"] {
          margin-right: var(--space-xs, 4px);
          cursor: pointer;
        }

        .radio-option label {
          margin: 0;
          cursor: pointer;
          color: var(--text-primary, #e5e5e5);
          font-size: var(--text-base, 1rem);
        }

        .visibility-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: var(--radius-sm, 4px);
          font-size: var(--text-xs, 0.75rem);
          margin-left: var(--space-xs, 4px);
        }

        .badge-show {
          background: var(--badge-show, #10b981);
          color: white;
        }

        .badge-secret {
          background: var(--badge-secret, #ef4444);
          color: white;
        }

        .actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-sm, 8px);
          margin-top: var(--space-lg, 24px);
        }

        button {
          padding: var(--space-sm, 8px) var(--space-md, 16px);
          border-radius: var(--radius-sm, 4px);
          font-size: var(--text-base, 1rem);
          cursor: pointer;
          transition: var(--transition-fast, 150ms ease-in-out);
          border: none;
        }

        .btn-cancel {
          background: var(--bg-surface, #1a1a1a);
          color: var(--text-primary, #e5e5e5);
          border: 1px solid var(--border-default, #444);
        }

        .btn-cancel:hover {
          background: var(--bg-elevated, #2a2a2a);
        }

        .btn-save {
          background: var(--color-primary, #3b82f6);
          color: white;
        }

        .btn-save:hover {
          background: #2563eb;
        }

        .btn-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          color: var(--color-danger, #ef4444);
          font-size: var(--text-sm, 0.875rem);
          margin-top: var(--space-xs, 4px);
        }
      </style>

      <div class="dialog">
        <div class="header">
          <h2>${this.editMode ? 'Edit Note' : 'Add Note'}</h2>
          <button class="close-btn" id="close-btn">&times;</button>
        </div>

        <div class="form-group">
          <label for="note-content">Note Content</label>
          <textarea
            id="note-content"
            placeholder="Enter your note here..."
            maxlength="${this.maxLength}"
          ></textarea>
          <div class="char-counter">
            <span id="char-count">0</span> / ${this.maxLength}
          </div>
          <div id="error-message" class="error-message" style="display: none;"></div>
        </div>

        <div class="form-group">
          <label>Visibility</label>
          <div class="visibility-group">
            <div class="radio-option">
              <input type="radio" id="visibility-show" name="visibility" value="${CONFIG.VISIBILITY_SHOW}" checked>
              <label for="visibility-show">
                Show to Players
                <span class="visibility-badge badge-show">Show</span>
              </label>
            </div>
            <div class="radio-option">
              <input type="radio" id="visibility-secret" name="visibility" value="${CONFIG.VISIBILITY_SECRET}">
              <label for="visibility-secret">
                GM Secret
                <span class="visibility-badge badge-secret">Secret</span>
              </label>
            </div>
          </div>
        </div>

        <div class="actions">
          <button class="btn-cancel" id="cancel-btn">Cancel</button>
          <button class="btn-save" id="save-btn">Save Note</button>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const textarea = this.shadowRoot.getElementById('note-content');
    const charCount = this.shadowRoot.getElementById('char-count');
    const errorMessage = this.shadowRoot.getElementById('error-message');

    // Use EventManager helpers for common patterns
    this.addEscapeToClose(() => this.close());
    this.addClickOutsideToClose(() => this.close());
    this.addCloseButton('#close-btn, #cancel-btn', () => this.close());

    // Character counter - using managed listener on shadowRoot element
    if (textarea) {
      this.addManagedListener(textarea, 'input', () => {
        const length = textarea.value.length;
        charCount.textContent = length;

        // Update counter color
        const counter = this.shadowRoot.querySelector('.char-counter');
        counter.classList.remove('warning', 'error');

        if (length > this.maxLength * 0.95) {
          counter.classList.add('error');
        } else if (length > this.maxLength * 0.8) {
          counter.classList.add('warning');
        }

        // Clear error message when typing
        errorMessage.style.display = 'none';
      });
    }

    // Save handler
    const saveBtn = this.shadowRoot.getElementById('save-btn');
    if (saveBtn) {
      this.addManagedListener(saveBtn, 'click', () => this.handleSave());
    }
  }

  handleSave() {
    const textarea = this.shadowRoot.getElementById('note-content');
    const errorMessage = this.shadowRoot.getElementById('error-message');
    const content = textarea.value.trim();

    // Validation
    if (!content) {
      errorMessage.textContent = 'Note content is required';
      errorMessage.style.display = 'block';
      return;
    }

    if (content.length > this.maxLength) {
      errorMessage.textContent = `Note exceeds ${this.maxLength} character limit`;
      errorMessage.style.display = 'block';
      return;
    }

    // Get visibility
    const visibility = this.shadowRoot.querySelector('input[name="visibility"]:checked').value;

    // Get current game date from session
    const session = window.currentSession;
    const gameDate = session?.calendar?.currentDate
      ? { year: session.calendar.currentDate.year, month: session.calendar.currentDate.month, day: session.calendar.currentDate.day }
      : null;

    // Create note data
    const noteData = {
      id: this.noteData?.id || 'note-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11),
      content: content,
      visibility: visibility,
      gameDate: this.noteData?.gameDate || gameDate,
      createdAt: this.noteData?.createdAt || Date.now(),
      updatedAt: Date.now()
    };

    // Dispatch save event using HexEvents system
    this.dispatchEvent(createHexEvent(HexEvents.NOTE_SAVED, {
      hexCoord: this.hexCoord,
      note: noteData,
      isEdit: this.editMode
    }));

    this.close();
  }

  open(hexCoord, existingNote = null) {
    this.hexCoord = hexCoord;
    this.noteData = existingNote;
    this.editMode = !!existingNote;

    // Update dialog title
    const title = this.shadowRoot.querySelector('h2');
    if (title) {
      title.textContent = this.editMode ? 'Edit Note' : 'Add Note';
    }

    // Populate form if editing
    if (existingNote) {
      const textarea = this.shadowRoot.getElementById('note-content');
      const charCount = this.shadowRoot.getElementById('char-count');

      if (textarea) {
        textarea.value = existingNote.content || '';
        charCount.textContent = textarea.value.length;
      }

      // Set visibility
      const visibilityRadio = this.shadowRoot.querySelector(
        `input[name="visibility"][value="${existingNote.visibility || CONFIG.VISIBILITY_SHOW}"]`
      );
      if (visibilityRadio) {
        visibilityRadio.checked = true;
      }
    } else {
      // Clear form for new note
      const textarea = this.shadowRoot.getElementById('note-content');
      const charCount = this.shadowRoot.getElementById('char-count');

      if (textarea) {
        textarea.value = '';
        charCount.textContent = '0';
      }

      // Reset visibility to "show"
      const showRadio = this.shadowRoot.getElementById('visibility-show');
      if (showRadio) {
        showRadio.checked = true;
      }
    }

    // Clear any error messages
    const errorMessage = this.shadowRoot.getElementById('error-message');
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }

    this.setAttribute('open', '');

    // Focus textarea
    setTimeout(() => {
      const textarea = this.shadowRoot.getElementById('note-content');
      textarea?.focus();
    }, 100);
  }

  close() {
    this.removeAttribute('open');
    this.hexCoord = null;
    this.noteData = null;
    this.editMode = false;
  }
}

customElements.define('note-editor', NoteEditor);
